export const LOONOPZAND_PERIOD_COLORS = ["#7F56D9", "#3B82F6", "#10B981", "#F59E0B"];
const PURPLE_PALETTE = LOONOPZAND_PERIOD_COLORS;

function wrapCategoryLabel(label, maximumLineLength = 28) {
  const words = String(label || "").split(/\s+/).filter(Boolean);
  const lines = [];

  words.forEach((word) => {
    const currentLine = lines[lines.length - 1];
    if (!currentLine || `${currentLine} ${word}`.length > maximumLineLength) {
      lines.push(word);
    } else {
      lines[lines.length - 1] = `${currentLine} ${word}`;
    }
  });

  return lines.length > 1 ? lines : (lines[0] || "");
}

export function buildPeriodOptions(categories = []) {
  return {
    chart: { type: "bar", toolbar: { show: false } },
    colors: [PURPLE_PALETTE[0]],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "52%" } },
    xaxis: { categories },
    dataLabels: { enabled: true },
    yaxis: { min: 0, forceNiceScale: true },
    tooltip: { y: { formatter: (value) => `${value} respondenten` } },
  };
}

export function buildHorizontalChoiceOptions(categories = [], mode = "abs") {
  return {
    chart: { type: "bar", toolbar: { show: false } },
    colors: PURPLE_PALETTE,
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "70%",
      },
    },
    xaxis: {
      categories,
      min: 0,
      ...(mode === "rel" ? { max: 100 } : {}),
      labels: {
        formatter: (value) =>
          mode === "rel" ? `${Number(value).toFixed(0)}%` : `${Number(value).toFixed(0)}`,
      },
    },
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (value) =>
          mode === "rel" ? `${Number(value).toFixed(1)}%` : `${value}`,
      },
    },
  };
}

export function buildThickMultiChoiceOptions(
  categories = [],
  series = [],
  details = [],
  mode = "abs"
) {
  const maximum = Math.max(
    0,
    ...series.flatMap((item) => item.data.map((value) => Number(value) || 0))
  );
  const axisMaximum = mode === "rel"
    ? Math.max(20, Math.ceil(maximum + 20))
    : Math.max(1, Math.ceil(maximum * 1.3));

  return {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: PURPLE_PALETTE,
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 3,
        hideZeroBarsWhenGrouped: true,
        // Fill almost the complete category band. With several period series
        // Apex divides this space between the bars, keeping each bar thick
        // without reintroducing the large vertical gaps.
        barHeight: "88%",
        dataLabels: { position: "top" },
      },
    },
    xaxis: {
      categories: categories.map((category) => wrapCategoryLabel(category)),
      min: 0,
      max: axisMaximum,
      labels: {
        formatter: (value) =>
          mode === "rel"
            ? `${Number(value).toFixed(0)}%`
            : `${Number(value).toFixed(0)}`,
      },
    },
    yaxis: {
      labels: {
        maxWidth: 190,
        style: { fontSize: "12px", colors: ["#374151"] },
      },
    },
    fullscreenKeepBaseTypography: true,
    fullscreenOverrides: {
      xaxis: {
        categories: categories.map((category) => wrapCategoryLabel(category, 38)),
        labels: {
          style: { fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          maxWidth: 320,
          trim: false,
          style: {
            fontSize: "11px",
            colors: ["#374151"],
          },
        },
      },
      legend: {
        fontSize: "12px",
      },
      dataLabels: {
        style: {
          fontSize: "11px",
          fontWeight: 600,
          colors: ["#374151"],
        },
      },
    },
    grid: { padding: { right: 24 } },
    legend: { position: "bottom", fontSize: "13px" },
    dataLabels: {
      enabled: true,
      // Apex pulls short-bar labels back to the zero axis when it calculates
      // them with a `start` anchor. Calculate with `end` to avoid that clamp;
      // dashboard CSS still renders the text from left to right after the bar.
      textAnchor: "end",
      offsetX: 3,
      formatter: (_value, options) => {
        const detail = details?.[options.seriesIndex]?.[options.dataPointIndex];
        if (!detail) {
          return "";
        }
        const percentage = `${Number(detail.percentage).toFixed(1)}%`;
        const separator = "\u00A0\u00A0";
        return mode === "rel"
          ? `${percentage}${separator}${detail.count}`
          : `${detail.count}${separator}${percentage}`;
      },
      style: {
        fontSize: "12px",
        fontWeight: 600,
        colors: ["#374151"],
      },
      background: { enabled: false },
    },
    tooltip: {
      y: {
        formatter: (_value, options) => {
          const detail = details?.[options.seriesIndex]?.[options.dataPointIndex];
          if (!detail) {
            return "";
          }
          return `${detail.count} respondenten (${Number(detail.percentage).toFixed(1)}%)`;
        },
      },
    },
  };
}
